import React, { useMemo, useState } from 'react';

import { FontAwesomeIcon } from '@appcharge/shared-ui';

import { cn } from '../../../../../components/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '../../../../../components/ui/collapsible';
import { schemaUtils } from '../../../integration-test-modal.utils';
import { FieldIcon } from '../schema-form-field.constants';
import { INestedObjectFieldProps } from '../schema-form-field.types';
import { SchemaFormField } from '../SchemaFormField';

import { nestedObjectFieldUtils } from './nested-object-field.utils';

export const NestedObjectField: React.FC<INestedObjectFieldProps> = ({
  name,
  path,
  property,
  value,
  onChange,
  required,
  definitions,
  hideOptionalFields
}) => {
  const [isOpen, setIsOpen] = useState(true);

  const fieldLabel = useMemo(
    () => schemaUtils.formatFieldLabel(name, property.title),
    [property.title, name]
  );

  const requiredFields = useMemo(
    () => new Set(property.required || []),
    [property.required]
  );

  const propertyEntries = useMemo(() => {
    const entries = Object.entries(property.properties || {});
    return entries.sort(([nameA], [nameB]) => {
      const aIsRequired = requiredFields.has(nameA);
      const bIsRequired = requiredFields.has(nameB);
      
      if (aIsRequired === bIsRequired) {
        return 0;
      }
      return aIsRequired ? -1 : 1;
    });
  }, [property.properties, requiredFields]);

  const objectValue = useMemo(
    () => nestedObjectFieldUtils.normalizeObjectValue(value),
    [value]
  );

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="space-y-2">
      <CollapsibleTrigger className="flex items-center gap-1 text-p-s font-medium text-base-foreground">
        <FontAwesomeIcon
          icon={FieldIcon.CHEVRON_DOWN}
          className={cn(
            'h-3 w-3 transition-transform duration-200',
            !isOpen && '-rotate-90'
          )}
        />
        <span className="pl-2">
          {fieldLabel}{' '}
          {required && (
            <span className="text-base-accentForeground m-0.5 text-p-xs">(Required)</span>
          )}
        </span>
      </CollapsibleTrigger>
      {property.description && (
        <p className="text-p-xs text-base-mutedForeground ml-5">{property.description}</p>
      )}
      <CollapsibleContent
        className={cn(
          'overflow-hidden',
          'data-[state=open]:animate-collapsible-down',
          'data-[state=closed]:animate-collapsible-up'
        )}
      >
        <div className="ml-1.5 pl-5 border-l-2 border-base-border space-y-4">
          {propertyEntries.map(([nestedName, nestedProperty]) => (
            <SchemaFormField
              key={nestedName}
              name={nestedName}
              path={`${path}.${nestedName}`}
              property={nestedProperty}
              value={objectValue[nestedName]}
              onChange={onChange}
              required={requiredFields.has(nestedName)}
              definitions={definitions}
              hideOptionalFields={hideOptionalFields}
            />
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
